import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TicketHead } from '../../components/TicketHead';
import { getErrorMessage } from '../../lib/errors';
import { formatInstantDate } from '../../lib/format';
import { STATUS_LABEL, statusTone } from '../../lib/pipeline';
import { platformLabel } from '../../lib/platforms';
import type { AdminBusinessDetailRead, QuotaRead } from '../../types/api';
import { fetchAdminBusiness, updateAdminQuota } from './adminApi';

/** Which write the confirmation on screen is for. */
type PendingWrite = { kind: 'set'; value: number } | { kind: 'clear' };

/**
 * One business, and the only thing an operator can change about it.
 *
 * Everything else here is read-only by design: an operator adjusts an allowance
 * and closes flags. Captions, slides and review history stay as the pipeline and
 * the reviewer left them, so there is no control to edit them and no endpoint
 * behind one.
 */
export function AdminBusinessPanel({
  businessId,
  onClose,
}: {
  businessId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AdminBusinessDetailRead | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingWrite | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [effective, setEffective] = useState<QuotaRead | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const row = await fetchAdminBusiness(businessId, controller.signal);
        if (cancelled) return;
        setDetail(row);
        setLoadError(null);
        setDraft(row.quota_override === null ? '' : String(row.quota_override));
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoadError(getErrorMessage(caught, 'Could not load that business.'));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  function askToSet(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = Number(draft);
    if (draft.trim() === '' || !Number.isInteger(value) || value < 0) {
      setSaveError('Enter a whole number of posts, zero or more.');
      return;
    }
    setSaveError(null);
    setPending({ kind: 'set', value });
  }

  function commit() {
    if (pending === null || isSaving) return;
    setIsSaving(true);
    setSaveError(null);

    void (async () => {
      try {
        const quota = await updateAdminQuota(businessId, {
          monthly_generation_quota: pending.kind === 'set' ? pending.value : null,
        });
        setEffective(quota);
        setPending(null);
        setIsSaving(false);
        // The list row's own numbers are now stale; the detail is the truth.
        reload();
      } catch (caught) {
        setIsSaving(false);
        setSaveError(getErrorMessage(caught, 'That change did not save. Try again.'));
      }
    })();
  }

  if (loadError !== null) {
    return (
      <section className="admin-panel">
        <p className="form__error" role="alert">
          {loadError}
        </p>
        <div className="admin-panel__actions">
          <button className="button" type="button" onClick={reload}>
            Try again
          </button>
          <button className="button" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </section>
    );
  }

  if (detail === null) {
    return (
      <section className="admin-panel">
        <p className="kb-section__status" role="status">
          Loading that business
        </p>
      </section>
    );
  }

  return (
    <section className="admin-panel">
      <TicketHead label={`Business / ${detail.id.slice(0, 8)}`} timestamp={detail.created_at} />

      <div className="admin-panel__head">
        <h3 className="admin-panel__title">{detail.name}</h3>
        <button className="button button--ghost" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <dl className="spec-sheet">
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Used this month</dt>
          <dd className="spec-sheet__value">
            {detail.monthly_used} of {detail.quota_limit}
          </dd>
        </div>
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Allowance</dt>
          <dd className="spec-sheet__value">
            {detail.quota_override === null
              ? 'Deployment default'
              : `Support override — ${detail.quota_override}`}
          </dd>
        </div>
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Open flags</dt>
          <dd className="spec-sheet__value">{detail.open_flag_count}</dd>
        </div>
      </dl>

      <form className="admin-form" onSubmit={askToSet} noValidate>
        <label className="field">
          <span className="field__label">Monthly posts</span>
          <input
            className="field__input"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={draft}
            disabled={isSaving}
            onChange={(event) => setDraft(event.target.value)}
          />
          <span className="field__hint">
            Zero stops generation entirely. Clearing the override is a different thing: it hands the
            business back to the deployment default.
          </span>
        </label>

        {saveError ? (
          <p className="form__error" role="alert">
            {saveError}
          </p>
        ) : null}

        {effective ? (
          <p className="form__notice" role="status">
            Saved. This business now has {effective.limit} posts a month, {effective.used} used and{' '}
            {effective.remaining} left.
          </p>
        ) : null}

        <div className="admin-panel__actions">
          <button className="button button--primary" type="submit" disabled={isSaving}>
            Save allowance
          </button>
          <button
            className="button"
            type="button"
            disabled={isSaving || detail.quota_override === null}
            onClick={() => setPending({ kind: 'clear' })}
          >
            Clear override
          </button>
        </div>
      </form>

      <div className="admin-panel__requests">
        <h4 className="admin-panel__subtitle">Recent requests</h4>
        {detail.recent_requests.length === 0 ? (
          <p className="admin-empty">Nothing generated yet.</p>
        ) : (
          <ul className="admin-request-list">
            {detail.recent_requests.map((request) => (
              <li key={request.id} className="admin-request">
                <span className="admin-request__id">{request.id.slice(0, 8)}</span>
                <span className="admin-request__meta">
                  {platformLabel(request.platform)} · {formatInstantDate(request.created_at)}
                  {request.attempt > 1 ? ` · attempt ${request.attempt}` : ''}
                </span>
                <span className={`status-chip status-chip--${statusTone(request.status)}`}>
                  {STATUS_LABEL[request.status]}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pending ? (
        <ConfirmDialog
          title={pending.kind === 'set' ? 'Change this allowance' : 'Clear this override'}
          message={
            pending.kind === 'set'
              ? `${detail.name} will get ${pending.value} posts a month until someone changes it again. The change is recorded against your account.`
              : `${detail.name} goes back to the deployment default allowance. The change is recorded against your account.`
          }
          confirmLabel={pending.kind === 'set' ? 'Set allowance' : 'Clear override'}
          busyLabel="Saving"
          dismissLabel="Cancel"
          isBusy={isSaving}
          error={saveError}
          onConfirm={commit}
          onDismiss={() => setPending(null)}
        />
      ) : null}
    </section>
  );
}
